import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Paper } from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import { customized_style } from 'shared/config/Styles';
import DynamicToggleButton from 'shared/ui/ToggleButton';
import ThemeButton from 'shared/ui/Button';
import { PageHeader } from 'shared/ui/HeaderIcon';
import CustomSnackbar from 'shared/ui/CustomSnackbar';

import ClientDataGrid from './components/ClientDataGrid';

/** Tab configuration for toggle buttons */
const TAB_LIST = [
    { content: 'TEAM SETTING', value: 'team_setting' },
    { content: 'CLIENT ASSIGNMENTS', value: 'client_assign' },
];

export default function ClientSetting() {
    /** Destructuring values from useAuth0 hook */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Loading state for API responses */
    const [loaded, setLoaded] = useState(false);
    /** Current selected tab value */
    const [tabValue, setTabValue] = useState('team_setting');

    /** Team setting data for datagrid */
    const [teamSettingRows, setTeamSettingRows] = useState([]);
    /** Client assignment data for datagrid */
    const [clientAssignmentRows, setClientAssignmentRows] = useState([]);
    /** Subclient metadata for dialog. */
    const [subClientRows, setSubClientRows] = useState([]);

    /** Trade partner metadata. */
    const [tradePartner, setTradePartner] = useState([]);
    /** Team metadata for dialog. */
    const [teamList, setTeamList] = useState([]);
    /** Sub client meta data for datagrid. */
    const [subClientList, setSubClientList] = useState([]);

    const [subClientDialogOpen, setSubClientDialogOpen] = useState(false);

    /** Set document title on component mount */
    useEffect(() => {
        document.title = 'Client Setting | Operation System';
    }, []);

    /**
     * Handle tab change and update loading state
     * @param {object} event - Change event
     * @param {string} newValue - New tab value
     */
    const handleTabChange = (event, newValue) => {
        if (newValue) {
            setLoaded(false);
            setTabValue(newValue);
        }
    };

    /** Add new team row to team setting table. */
    const handleNewClick = () => {
        const newRow = {
            id: randomId(),
            name: '',
            il_trade_partner_id: [],
            status: true,
        };

        if (window.confirm('Are you sure you want to add a new team? Once added, it cannot be deleted.')) {
            callAPI({
                method: 'post',
                module: 'client_setting',
                component: 'team_setting',
                getAccessTokenSilently,
                email: user.email,
                obj: JSON.stringify(newRow),
                onSuccess: (resp) => {
                    setTeamSettingRows((prev) => [...prev, resp.data]);
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'New team has been successfully created.',
                            severity: 'success',
                        })
                    );
                },
                onError: () =>
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Something went wrong, please try again.',
                            severity: 'error',
                        })
                    ),
            });
        }
    };

    /** Common props for ClientDataGrid. */
    const dataGridProps = {
        type: tabValue === 'team_setting' ? 'team' : 'client',
        loaded,
        setLoaded,
        tabValue,
        teamSettingRows,
        setTeamSettingRows,
        clientAssignmentRows,
        setClientAssignmentRows,
        tradePartner,
        setTradePartner,
        // Client assignment specific props
        teamList,
        setTeamList,
        subClientList,
        setSubClientList,
        subClientRows,
        setSubClientRows,
        subClientDialogOpen,
        setSubClientDialogOpen,
    };

    return (
        <Paper sx={customized_style.itemPaper}>
            <PageHeader title={'CLIENT SETTING'} icon={SettingsIcon} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <DynamicToggleButton
                    data={TAB_LIST}
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{ width: '150px' }}
                />
                {tabValue === 'team_setting' && (
                    <ThemeButton text="NEW" icon={<AddIcon />} width={'68px'} onClick={handleNewClick} />
                )}
            </Box>
            <ClientDataGrid {...dataGridProps} />
            <CustomSnackbar />
        </Paper>
    );
}
